import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from the OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    return res.data;
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    const res = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async (reason = "") => {
    try {
      await api.post(`/auth/logout${reason ? `?reason=${reason}` : ""}`);
    } catch (e) {
      // Local logout must proceed even if the network request fails.
    }
    setUser(null);
  }, []);

  // Client-side mirror of backend has_perm — used ONLY for UI visibility.
  // Every API endpoint re-verifies permission server-side.
  const can = useCallback(
    (module, action = "view") => {
      if (!user) return false;
      if (user.role === "super_admin") return true;
      if (user.status && user.status !== "active") return false;
      if (user.role === "full_admin") return module !== "access";
      return Boolean(user.permissions?.[module]?.[action]);
    },
    [user]
  );

  const value = useMemo(
    () => ({ user, loading, login, googleLogin, logout, can }),
    [user, loading, login, googleLogin, logout, can]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
